import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Settings, Clock, User, Plus, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useReportShares, CreateShareInput } from "@/hooks/useReportShares";
import { useReportShareDialog } from "@/contexts/ReportShareDialogContext";
import { ScheduleConfigPanel } from "./ScheduleConfigPanel";
import { ShareTypeConfig } from "./ShareTypeConfig";

interface Recipient {
  name: string;
  email: string;
}

const SHARE_TYPES = [
  { value: 'client_portal', label: 'Client Portal' },
  { value: 'site_audit', label: 'Site Audit Report' },
  { value: 'personal_card', label: 'Personal Card' },
  { value: 'pdf_access', label: 'PDF Document' },
  { value: 'proposal', label: 'Sales Proposal' },
];

export function GlobalReportShareDialog() {
  const { state, closeDialog } = useReportShareDialog();
  const { isOpen, editingShare, onSuccessCallback } = state;
  const { createShare, updateShare, createSchedule, updateSchedule } = useReportShares();
  
  const [name, setName] = useState('');
  const [shareType, setShareType] = useState('client_portal');
  const [recipients, setRecipients] = useState<Recipient[]>([{ name: '', email: '' }]);
  const [usePin, setUsePin] = useState(false);
  const [accessPin, setAccessPin] = useState('');
  const [useExpiry, setUseExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState<Date | undefined>(undefined);
  const [reportConfig, setReportConfig] = useState<Record<string, any>>({});
  const [activeTab, setActiveTab] = useState('details');

  // Schedule state
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleType, setScheduleType] = useState('weekly');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleDays, setScheduleDays] = useState<number[]>([1]);
  const [scheduleDayOfMonth, setScheduleDayOfMonth] = useState(1);
  const [timezone, setTimezone] = useState('Europe/Athens');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailTemplate, setEmailTemplate] = useState('');

  // Sync state when dialog opens or editingShare changes
  useEffect(() => {
    if (isOpen) {
      setName(editingShare?.name || '');
      setShareType(editingShare?.share_type || 'client_portal');
      
      // Parse recipients
      if (editingShare?.report_config?.recipients) {
        setRecipients(editingShare.report_config.recipients);
      } else if (editingShare?.recipient_email) {
        setRecipients([{ name: editingShare.recipient_name || '', email: editingShare.recipient_email }]);
      } else {
        setRecipients([{ name: '', email: '' }]);
      }
      
      setUsePin(!!editingShare?.access_pin);
      setAccessPin(editingShare?.access_pin || '');
      setUseExpiry(!!editingShare?.expires_at);
      setExpiryDate(editingShare?.expires_at ? new Date(editingShare.expires_at) : undefined);
      setReportConfig(editingShare?.report_config || {});
      setActiveTab('details');
      
      // Schedule state
      setScheduleEnabled(!!editingShare?.schedule?.is_enabled);
      setScheduleType(editingShare?.schedule?.schedule_type || 'weekly');
      setScheduleTime(editingShare?.schedule?.schedule_time || '09:00');
      setScheduleDays(editingShare?.schedule?.schedule_days || [1]);
      setScheduleDayOfMonth(editingShare?.schedule?.schedule_day_of_month || 1);
      setTimezone(editingShare?.schedule?.timezone || 'Europe/Athens');
      setEmailSubject(editingShare?.schedule?.email_subject || '');
      setEmailTemplate(editingShare?.schedule?.email_template || '');
    }
  }, [isOpen, editingShare]);

  const addRecipient = () => {
    setRecipients([...recipients, { name: '', email: '' }]);
  };

  const removeRecipient = (index: number) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== index));
    }
  };

  const updateRecipient = (index: number, field: 'name' | 'email', value: string) => {
    const updated = [...recipients];
    updated[index] = { ...updated[index], [field]: value };
    setRecipients(updated);
  };

  const handleSubmit = async () => {
    // Filter out empty recipients
    const validRecipients = recipients.filter(r => r.email.trim());
    
    const shareData: CreateShareInput = {
      name,
      share_type: shareType,
      recipient_email: validRecipients[0]?.email || undefined,
      recipient_name: validRecipients[0]?.name || undefined,
      access_pin: usePin ? accessPin : undefined,
      expires_at: useExpiry && expiryDate ? expiryDate.toISOString() : undefined,
      report_config: { ...reportConfig, recipients: validRecipients },
    };

    if (editingShare) {
      await updateShare.mutateAsync({ id: editingShare.id, ...shareData });
      
      if (scheduleEnabled) {
        const scheduleData = {
          schedule_type: scheduleType,
          schedule_time: scheduleTime,
          schedule_days: scheduleDays,
          schedule_day_of_month: scheduleDayOfMonth,
          timezone,
          email_subject: emailSubject,
          email_template: emailTemplate,
          is_enabled: true,
        };

        if (editingShare.schedule) {
          await updateSchedule.mutateAsync({ id: editingShare.schedule.id, ...scheduleData });
        } else {
          await createSchedule.mutateAsync({ share_id: editingShare.id, ...scheduleData });
        }
      }
    } else {
      const newShare = await createShare.mutateAsync(shareData);
      
      if (scheduleEnabled && newShare) {
        await createSchedule.mutateAsync({
          share_id: newShare.id,
          schedule_type: scheduleType,
          schedule_time: scheduleTime,
          schedule_days: scheduleDays,
          schedule_day_of_month: scheduleDayOfMonth,
          timezone,
          email_subject: emailSubject,
          email_template: emailTemplate,
          is_enabled: true,
        });
      }
    }

    onSuccessCallback?.();
    closeDialog();
  };

  const isSubmitting = createShare.isPending || updateShare.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()} modal={true}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug
            popupName={editingShare ? "Edit Report Share" : "Create Report Share"}
            popupContext="/report-shares"
            isOpen={isOpen}
          >
            {editingShare ? 'Edit Report Share' : 'Create Report Share'}
          </DialogTitleWithBug>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Report Config
            </TabsTrigger>
            <TabsTrigger value="schedule" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Schedule
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Share Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Monthly Report for Client X"
              />
            </div>

            <div className="space-y-2">
              <Label>Share Type *</Label>
              <Select value={shareType} onValueChange={setShareType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {SHARE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Recipients</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addRecipient}
                  className="flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  Add Recipient
                </Button>
              </div>
              
              <div className="space-y-2">
                {recipients.map((recipient, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={recipient.name}
                      onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                      placeholder="Name"
                      className="flex-1"
                    />
                    <Input
                      type="email"
                      value={recipient.email}
                      onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                      placeholder="email@example.com"
                      className="flex-1"
                    />
                    {recipients.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRecipient(index)}
                        className="h-9 w-9 text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between border rounded-lg p-4">
              <div className="space-y-0.5">
                <Label>PIN Protection</Label>
                <p className="text-sm text-muted-foreground">
                  Require a 6-digit PIN to access the report
                </p>
              </div>
              <Switch checked={usePin} onCheckedChange={setUsePin} />
            </div>

            {usePin && (
              <div className="space-y-2">
                <Label>Access PIN (6 digits)</Label>
                <Input
                  value={accessPin}
                  onChange={(e) => setAccessPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength={6}
                />
              </div>
            )}

            <div className="flex items-center justify-between border rounded-lg p-4">
              <div className="space-y-0.5">
                <Label>Set Expiry Date</Label>
                <p className="text-sm text-muted-foreground">
                  Link will expire after this date
                </p>
              </div>
              <Switch checked={useExpiry} onCheckedChange={setUseExpiry} />
            </div>

            {useExpiry && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !expiryDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {expiryDate ? format(expiryDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={expiryDate}
                    onSelect={setExpiryDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            )}
          </TabsContent>

          <TabsContent value="config" className="mt-4">
            <ShareTypeConfig
              shareType={shareType}
              config={reportConfig}
              onChange={setReportConfig}
            />
          </TabsContent>

          <TabsContent value="schedule" className="mt-4">
            <ScheduleConfigPanel
              enabled={scheduleEnabled}
              onEnabledChange={setScheduleEnabled}
              scheduleType={scheduleType}
              onScheduleTypeChange={setScheduleType}
              scheduleTime={scheduleTime}
              onScheduleTimeChange={setScheduleTime}
              scheduleDays={scheduleDays}
              onScheduleDaysChange={setScheduleDays}
              scheduleDayOfMonth={scheduleDayOfMonth}
              onScheduleDayOfMonthChange={setScheduleDayOfMonth}
              timezone={timezone}
              onTimezoneChange={setTimezone}
              emailSubject={emailSubject}
              onEmailSubjectChange={setEmailSubject}
              emailTemplate={emailTemplate}
              onEmailTemplateChange={setEmailTemplate}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={closeDialog}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name || !shareType || isSubmitting}>
            {isSubmitting ? 'Saving...' : editingShare ? 'Update Share' : 'Create Share'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
